import { useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDisposals, usePendingDisposals } from "@/features/disposals/api";
import { DISPOSAL_MODES, formatINR } from "@/features/disposals/types";
import { DisposalModeBadge } from "@/features/disposals/badges";
import { useOptionValues } from "@/features/configuration/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked } from "@/lib/roles";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/disposals/")({
  component: DisposalsIndex,
});

const WRITER = new Set(["super_admin", "biomed_head", "finance"]);

function DisposalsIndex() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const canCreate = (me?.roles ?? []).some((r) => WRITER.has(r));
  const [tab, setTab] = useState("pending");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Disposals</h1> <ReadOnlyPill module="disposals" />
        <p className="text-sm text-muted-foreground">
          Condemned assets pending disposal, and completed disposal records.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending Disposal</TabsTrigger>
          <TabsTrigger value="register">Disposal Register</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-3">
          <PendingTab canCreate={canCreate} onRecord={(assetId) => navigate({ to: "/disposals/new", search: { assetId } })} />
        </TabsContent>

        <TabsContent value="register" className="mt-3">
          <RegisterTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PendingTab({ canCreate, onRecord }: { canCreate: boolean; onRecord: (id: string) => void }) {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = usePendingDisposals(effectiveBranchId);
  return (
    <Card>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No assets pending disposal.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset Code</TableHead>
                <TableHead>Asset Name</TableHead>
                <TableHead>Asset Type</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Condemnation No</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Repair Est.</TableHead>
                <TableHead className="text-right">Replacement Est.</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.asset_id}>
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell className="text-sm">{r.asset_name}</TableCell>
                  <TableCell className="text-sm">{r.asset_type ?? "—"}</TableCell>
                  <TableCell className="text-sm">{r.department_name ?? "—"}</TableCell>
                  <TableCell className="text-sm">{r.location_name ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.request_no ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.approved_at ? formatDate(r.approved_at) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.reason ?? "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums text-right">{formatINR(r.repair_cost_estimate)}</TableCell>
                  <TableCell className="text-sm tabular-nums text-right">{formatINR(r.replacement_cost_estimate)}</TableCell>
                  <TableCell className="text-right">
                    {canCreate ? (
                      <Button size="sm" onClick={() => onRecord(r.asset_id)}>Record Disposal</Button>
                    ) : (
                      <Badge variant="secondary">View only</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function RegisterTab() {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const [mode, setMode] = useState<string>("all");
  const [search, setSearch] = useState("");
  const { values: modeOpts } = useOptionValues("disposal", "disposal_method");
  const modeChoices = modeOpts.length ? modeOpts.map((o) => o.value) : [...DISPOSAL_MODES];
  const { data: rows = [], isLoading } = useDisposals({
    mode: mode === "all" ? null : mode,
    search,
    branchId: effectiveBranchId,
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input className="w-56" placeholder="Search asset / buyer…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                {modeChoices.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No disposals recorded.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Disposal No</TableHead>
                  <TableHead>Asset Code</TableHead>
                  <TableHead>Asset Name</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Buyer / Recycler / Agency</TableHead>
                  <TableHead className="text-right">Sale Value</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead>Disposal Date</TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell className="font-mono text-xs">{r.asset?.asset_code ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.asset?.name ?? "—"}</TableCell>
                    <TableCell><DisposalModeBadge mode={r.mode} /></TableCell>
                    <TableCell className="text-sm">{r.buyer_name ?? r.recycler_reg_no ?? "—"}</TableCell>
                    <TableCell className="text-sm tabular-nums text-right">{formatINR(r.sale_value)}</TableCell>
                    <TableCell className="text-sm tabular-nums text-right">{formatINR(r.gst_on_sale)}</TableCell>
                    <TableCell className="text-sm">{r.disposal_date}</TableCell>
                    <TableCell className="text-sm">
                      {r.certificate_url ? (
                        <a href={r.certificate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                          <FileText className="h-3 w-3" /> View
                        </a>
                      ) : "—"}
                    </TableCell>
                    <TableCell><Badge variant="secondary">Disposed</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
